import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Link, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import { brand, neutral } from '../../theme';
import { formatFrameRate, formatTimecode } from '../../utils/timecode';
import { formatBytes, formatDate } from '../../utils/format';
import { downloadTextFile, namedDownloadUrl } from '../../utils/download';
import {
  AUDIO_SOURCE_KEY,
  NleAudioSource,
  ROUGH_CUT_FORMATS,
  RoughCutFormat,
  RoughCutInput,
  angleFileNames,
  buildRoughCutExport,
  roughCutProblem,
} from '../../utils/nleExport';
import { recordingDisplayName } from '../Multicam/separateAudio';
import Section from '../ui/Section';
import MarkerExportMenu from '../AnnotationStudio/MarkerExportMenu';
import FrameRateChip from '../AnnotationStudio/FrameRateChip';
import { isSwitch, ReviewAnnotation } from '../Multicam/multicamReview';
import type { ExportableAnnotation } from '../../utils/annotationExport';

export interface WorkspaceAsset {
  id?: string;
  position?: string | null;
  startTime?: number | null;
  endTime?: number | null;
  url: string;
  notes?: string | null;
}

export interface WorkspaceAngle {
  angleKey: string;
  label?: string | null;
  videoName?: string | null;
  videoUrl: string;
  durationMs?: number | null;
  timelineStartMs?: number | null;
  sourceInMs?: number | null;
  sourceOutMs?: number | null;
}

interface AssetsTabProps {
  projectName: string;
  videoName?: string | null;
  videoUrl?: string | null;
  durationSeconds: number;
  images: WorkspaceAsset[];
  sounds: WorkspaceAsset[];
  referenceLinks: string[];
  referenceFiles: string[];
  angles: WorkspaceAngle[];
  audioAngleKey?: string | null;
  /** The set's separate sound recording: the sound source, downloaded with the angles. */
  audioSource?: (NleAudioSource & { videoUrl?: string | null }) | null;
  /**
   * The creator's multicam cuts and notes, for the rough cut. Undefined until
   * they have loaded, so a set with no cuts is never confused with one whose
   * cuts are still on their way.
   */
  multicamAnnotations?: ReviewAnnotation[];
  markerAnnotations: ExportableAnnotation[];
  fps: number;
  /** The source frame rate was read from the file at upload. */
  fpsKnown: boolean;
  /** The user picked `fps` (only while the source rate is unknown). */
  fpsChosen: boolean;
  onFpsChange: (fps: number) => void;
  packageBusy: boolean;
  packageInfo?: { sizeBytes?: number; expiresAt?: string } | null;
  onDownloadPackage: () => void;
  onPreviewAsset: (asset: WorkspaceAsset, kind: 'image' | 'audio') => void;
  onDownloadAsset: (asset: WorkspaceAsset, kind: 'image' | 'audio') => void;
  assetBusy: Record<string, boolean>;
  onDownloadMulticamJson?: () => void;
  onDownloadAnnotationsJson?: () => void;
  onExported?: (filename: string) => void;
}

const fileNameFromUrl = (url: string) => {
  try {
    const last = new URL(url, window.location.origin).pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : url;
  } catch {
    return url;
  }
};

/** A neutral square for a row's file type; colour is kept for status and annotation categories. */
const FileIcon: React.FC<{ children: React.ReactNode; dark?: boolean }> = ({ children, dark = false }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 36,
      height: 36,
      flexShrink: 0,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 800,
      bgcolor: dark ? brand.ink : neutral[100],
      color: dark ? 'common.white' : brand.slate,
      border: dark ? 'none' : `1px solid ${brand.border}`,
      '& svg': { fontSize: 20 },
    }}
  >
    {children}
  </Box>
);

const rowSx = (first: boolean) => ({
  display: 'flex',
  alignItems: { xs: 'flex-start', sm: 'center' },
  flexWrap: { xs: 'wrap', sm: 'nowrap' },
  gap: 1.5,
  px: 2.5,
  py: 1.5,
  borderTop: first ? 'none' : `1px solid ${brand.border}`,
});

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const ROUGH_CUT_STEPS: Array<[string, string]> = [
  ['Before you import', 'Download every angle (and the separate audio, if there is one) into one folder. Each downloads under the name shown above; if your browser renamed one, change it back. Save the rough cut in the same folder.'],
  ['Premiere Pro', 'File → Import, then choose the .xml. When Premiere asks for the media, click Locate and choose the file it names; it finds the others in that folder.'],
  ['DaVinci Resolve', 'File → Import → Timeline, then choose the .xml or the .fcpxml. If Resolve asks where the clips are, choose the folder.'],
  ['Final Cut Pro', 'File → Import → XML, then choose the .fcpxml. Angles saved next to it link straight away; if any show as missing, choose File → Relink Files → Original Media and pick the folder.'],
];

interface RoughCutExportMenuProps {
  /** Null while the rough cut cannot be built. */
  input: RoughCutInput | null;
  fps: number;
  /** Estimated (not measured) frame rate: say so in the menu. */
  fpsAssumed: boolean;
  /** "3 cuts and 12 notes from the creator." */
  summary: string;
  onExported?: (filename: string) => void;
  onFailed: (failed: boolean) => void;
}

/** "Export rough cut" menu: the creator's cut as FCP7 XML or FCPXML. */
const RoughCutExportMenu: React.FC<RoughCutExportMenuProps> = ({ input, fps, fpsAssumed, summary, onExported, onFailed }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const run = (format: RoughCutFormat) => {
    setAnchor(null);
    if (!input) return;
    try {
      const file = buildRoughCutExport(format, input);
      downloadTextFile(file.filename, file.content, file.mimeType);
      onFailed(false);
      onExported?.(file.filename);
    } catch (error) {
      console.error('Failed to build the rough cut:', error);
      onFailed(true);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadOutlinedIcon />}
        endIcon={<ExpandMoreIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={!input}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
      >
        Export rough cut
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 300, maxWidth: 380 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Built at {formatFrameRate(fps)} fps{fpsAssumed ? ' (estimated — set the frame rate if your footage differs)' : ''}.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">{summary}</Typography>
        </Box>
        <Divider />
        {ROUGH_CUT_FORMATS.map((option) => (
          <MenuItem key={option.format} onClick={() => run(option.format)}>
            <ListItemText
              primary={option.label}
              secondary={option.description}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * Everything the editor takes into their editing app: the handoff package,
 * the source, each image/audio/reference file, multicam angles, and the
 * creator's notes as importable markers.
 */
const AssetsTab: React.FC<AssetsTabProps> = ({
  projectName,
  videoName,
  videoUrl,
  durationSeconds,
  images,
  sounds,
  referenceLinks,
  referenceFiles,
  angles,
  audioAngleKey,
  audioSource,
  multicamAnnotations,
  markerAnnotations,
  fps,
  fpsKnown,
  fpsChosen,
  onFpsChange,
  packageBusy,
  packageInfo,
  onDownloadPackage,
  onPreviewAsset,
  onDownloadAsset,
  assetBusy,
  onDownloadMulticamJson,
  onDownloadAnnotationsJson,
  onExported,
}) => {
  const media = [
    ...images.map((asset, index) => ({ asset, kind: 'image' as const, index })),
    ...sounds.map((asset, index) => ({ asset, kind: 'audio' as const, index })),
  ];
  const isMulticam = angles.length > 0;
  const packageSize = packageInfo?.sizeBytes && packageInfo.sizeBytes > 0 ? formatBytes(packageInfo.sizeBytes) : null;
  const packageExpiry = packageInfo?.expiresAt ? formatDate(packageInfo.expiresAt) : '';

  // The rough cut and the angle rows use the same file names, so what the
  // editor saves is what the rough cut looks for.
  const recording = audioSource?.videoUrl ? audioSource : null;
  const fileNames = useMemo(() => angleFileNames(angles, recording), [angles, recording]);
  const roughCut = useMemo(() => {
    if (!isMulticam || !multicamAnnotations) return { input: null, problem: null };
    const input: RoughCutInput = {
      title: projectName,
      angles,
      annotations: multicamAnnotations,
      audioAngleKey,
      audioSource: recording,
      timelineDurationMs: durationSeconds * 1000,
      fps,
    };
    const problem = roughCutProblem(input);
    return { input: problem ? null : input, problem };
  }, [isMulticam, multicamAnnotations, projectName, angles, audioAngleKey, recording, durationSeconds, fps]);
  const cutCount = (multicamAnnotations || []).filter((annotation) => isSwitch(annotation)).length;
  const noteCount = (multicamAnnotations || []).length - cutCount;
  const [roughCutFailed, setRoughCutFailed] = useState(false);
  let roughCutStatus: string | null = null;
  if (!multicamAnnotations) roughCutStatus = 'The rough cut is available once the creator’s cuts have loaded.';
  else if (roughCut.problem === 'no-length') roughCutStatus = 'This set’s length wasn’t recorded, so there is no timeline to export.';
  else if (roughCut.problem === 'no-angles') roughCutStatus = 'There are no angles to export.';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 400px' }, gap: 2.5, alignItems: 'start' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
        <Section
          id="handoff-package"
          title="Handoff package"
          description="Feedback by timecode, tasks, markers for your editing app and every asset, in one download."
        >
          <Box data-testid="handoff-package" sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={onDownloadPackage}
              disabled={packageBusy}
              startIcon={packageBusy ? <CircularProgress size={16} color="inherit" /> : <FolderZipOutlinedIcon />}
            >
              {packageBusy ? 'Preparing package…' : 'Download package (.zip)'}
            </Button>
            {packageSize && (
              <Typography variant="body2" color="text.secondary">
                Last package: {packageSize}
                {packageExpiry ? `, links valid until ${packageExpiry}` : ''}.
              </Typography>
            )}
          </Box>
        </Section>

        {!isMulticam && (
          <Section title="Source video" disablePadding>
            <Box sx={rowSx(true)}>
              <FileIcon><MovieOutlinedIcon /></FileIcon>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{videoName || 'Source video'}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {durationSeconds > 0 ? `${formatTimecode(durationSeconds, fps)} long` : 'Length unknown until it loads'}
                </Typography>
              </Box>
              <Button
                variant="text"
                size="small"
                startIcon={<FileDownloadOutlinedIcon />}
                href={videoUrl || undefined}
                target="_blank"
                rel="noopener"
                disabled={!videoUrl}
                {...({ download: videoName || true } as any)}
              >
                Download source
              </Button>
            </Box>
          </Section>
        )}

        {isMulticam && (
          <Section
            title={`Multicam angles (${angles.length})${recording ? ' and separate audio' : ''}`}
            action={(
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <RoughCutExportMenu
                  input={roughCut.input}
                  fps={fps}
                  fpsAssumed={!fpsKnown && !fpsChosen}
                  summary={`${plural(cutCount, 'cut')} and ${plural(noteCount, 'note')} from the creator.`}
                  onExported={onExported}
                  onFailed={setRoughCutFailed}
                />
                {onDownloadMulticamJson && (
                  <Button size="small" startIcon={<DataObjectOutlinedIcon />} onClick={onDownloadMulticamJson}>
                    multicam.json
                  </Button>
                )}
                {onDownloadAnnotationsJson && (
                  <Button size="small" startIcon={<DataObjectOutlinedIcon />} onClick={onDownloadAnnotationsJson}>
                    annotations.json
                  </Button>
                )}
              </Box>
            )}
            disablePadding
          >
            <Box component="ul" aria-label="Multicam angles" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {angles.map((angle, index) => {
                const fileName = fileNames[angle.angleKey];
                // Two cameras can both name a file C0001.MP4; the rough cut tells them apart.
                const renamed = Boolean(fileName) && fileName !== (angle.videoName || '');
                return (
                  <Box component="li" key={angle.angleKey} sx={rowSx(index === 0)}>
                    <FileIcon dark>{angle.angleKey}</FileIcon>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {angle.label || `Angle ${angle.angleKey}`}
                        {!recording && angle.angleKey === audioAngleKey && (
                          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · sound source</Box>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block" title={fileName || undefined}>
                        {renamed ? `Downloads as “${fileName}”` : angle.videoName}
                        {(angle.timelineStartMs || 0) > 0 ? ` · starts +${formatTimecode((angle.timelineStartMs || 0) / 1000, fps)}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<FileDownloadOutlinedIcon />}
                      // Saved under the name the rough cut expects, so it relinks without renaming.
                      href={namedDownloadUrl(angle.videoUrl, fileName || angle.videoName)}
                      target="_blank"
                      rel="noopener"
                      aria-label={`Download angle ${angle.angleKey}`}
                      {...({ download: fileName || true } as any)}
                    >
                      Download
                    </Button>
                  </Box>
                );
              })}
              {recording && (() => {
                // The recorder's file: the set's sound. Saved under the name the rough cut expects.
                const fileName = fileNames[AUDIO_SOURCE_KEY];
                const renamed = Boolean(fileName) && fileName !== (recording.videoName || '');
                const startMs = recording.timelineStartMs || 0;
                const trimMs = recording.sourceInMs || 0;
                return (
                  <Box component="li" key={AUDIO_SOURCE_KEY} sx={rowSx(false)} data-testid="separate-audio-row">
                    <FileIcon dark><AudioFileOutlinedIcon /></FileIcon>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {recordingDisplayName(recording)}
                        <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · sound source</Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block" title={fileName || undefined}>
                        {renamed ? `Downloads as “${fileName}”` : recording.videoName}
                        {startMs > 0 ? ` · starts +${formatTimecode(startMs / 1000, fps)}` : ''}
                        {startMs === 0 && trimMs > 0 ? ` · its first ${formatTimecode(trimMs / 1000, fps)} come before the master` : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<FileDownloadOutlinedIcon />}
                      href={namedDownloadUrl(recording.videoUrl || '', fileName || recording.videoName)}
                      target="_blank"
                      rel="noopener"
                      aria-label="Download the separate audio"
                      {...({ download: fileName || true } as any)}
                    >
                      Download
                    </Button>
                  </Box>
                );
              })()}
            </Box>
            <Box data-testid="rough-cut-help" sx={{ px: 2.5, py: 2, borderTop: `1px solid ${brand.border}` }}>
              <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
                Importing the rough cut
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                The creator’s cut as an edit you can open: their cuts on the first video track, the sound source
                underneath{recording ? ' (the separate audio)' : ''}, every angle in sync on its own track (switched
                off), and their notes as markers.
              </Typography>
              {roughCutStatus && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  {roughCutStatus}
                </Typography>
              )}
              {roughCutFailed && (
                <Typography variant="body2" color="error" role="alert" sx={{ mt: 1 }}>
                  The rough cut couldn’t be built. Try again, or export the markers instead.
                </Typography>
              )}
              {ROUGH_CUT_STEPS.map(([step, text]) => (
                <Box key={step} sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{step}</Typography>
                  <Typography variant="body2" color="text.secondary">{text}</Typography>
                </Box>
              ))}
            </Box>
          </Section>
        )}

        <Section title={`Images and audio (${media.length})`} disablePadding>
          {media.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
              No images or audio were attached to this project.
            </Typography>
          ) : (
            <Box component="ul" aria-label="Images and audio" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {media.map(({ asset, kind, index }, row) => {
                const key = `${kind}-${asset.id || index}`;
                const hasRange = typeof asset.startTime === 'number';
                const label = asset.notes || `${kind === 'image' ? 'Image' : 'Audio'} ${index + 1}`;
                return (
                  <Box component="li" key={key} sx={rowSx(row === 0)}>
                    <FileIcon>{kind === 'image' ? <ImageOutlinedIcon /> : <AudioFileOutlinedIcon />}</FileIcon>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {hasRange
                          ? `${formatTimecode(asset.startTime as number, fps)}${typeof asset.endTime === 'number' && asset.endTime > (asset.startTime as number) ? ` → ${formatTimecode(asset.endTime, fps)}` : ''}`
                          : 'No placement given'}
                        {asset.position ? ` · ${asset.position}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => onPreviewAsset(asset, kind)} aria-label={`Preview ${kind} ${index + 1}`}>
                        Preview
                      </Button>
                      <Button
                        size="small"
                        startIcon={assetBusy[key] ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlinedIcon />}
                        disabled={assetBusy[key]}
                        onClick={() => onDownloadAsset(asset, kind)}
                        aria-label={`Download ${kind} ${index + 1}`}
                      >
                        Download
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Section>

        <Section title="References" disablePadding>
          {referenceLinks.length + referenceFiles.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
              The creator didn&apos;t add reference links or files.
            </Typography>
          ) : (
            <Box component="ul" aria-label="References" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {referenceLinks.map((link, index) => (
                <Box component="li" key={`link-${link}`} sx={rowSx(index === 0)}>
                  <LinkOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-hidden="true" />
                  <Link href={link} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ wordBreak: 'break-all', fontWeight: 600, minWidth: 0 }}>
                    {link}
                  </Link>
                </Box>
              ))}
              {referenceFiles.map((file, index) => (
                <Box component="li" key={`file-${file}`} sx={rowSx(referenceLinks.length === 0 && index === 0)}>
                  <AttachFileOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-hidden="true" />
                  <Link href={file} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ wordBreak: 'break-all', fontWeight: 600, minWidth: 0 }}>
                    {fileNameFromUrl(file)}
                  </Link>
                </Box>
              ))}
            </Box>
          )}
        </Section>
      </Box>

      <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, minWidth: 0 }} data-testid="marker-export">
        <Section
          title="Markers for your editing app"
          description={`The creator's ${markerAnnotations.length} note${markerAnnotations.length === 1 ? '' : 's'} as timeline markers, so each one lands on its frame.`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <MarkerExportMenu
              annotations={markerAnnotations}
              fps={fps}
              fpsAssumed={!fpsKnown && !fpsChosen}
              title={projectName}
              sourceName={videoName || undefined}
              label="Export markers"
              variant="outlined"
              onExported={onExported}
            />
            <FrameRateChip fps={fps} known={fpsKnown} chosen={fpsChosen} onChange={onFpsChange} />
          </Box>
          {markerAnnotations.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              There are no notes to export yet.
            </Typography>
          )}
          <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, pt: 2, borderTop: `1px solid ${brand.border}` }}>
            Importing the markers
          </Typography>
          {[
            ['DaVinci Resolve', 'Timeline → Import → Timeline Markers from EDL. Use the “1-hour start” EDL for timelines that start at 01:00:00:00 (Resolve’s default).'],
            ['Premiere Pro', 'The marker CSV uses Premiere’s marker-list columns (Name, In, Out, Duration). Keep it open beside your sequence as a checklist, or bring it in with a marker-import extension.'],
            ['Final Cut Pro', 'Final Cut has no EDL or CSV marker import; work from the CSV, or use the handoff package’s feedback list.'],
            ['Frame rate', 'Set the rate above to match your sequence before exporting, so markers land on the right frame.'],
          ].map(([app, text]) => (
            <Box key={app} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{app}</Typography>
              <Typography variant="body2" color="text.secondary">{text}</Typography>
            </Box>
          ))}
        </Section>
      </Box>
    </Box>
  );
};

export default AssetsTab;
